import { Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import {
  useAssetProviderDisplayName,
  useIsAssetProviderIntegrationEnabled,
} from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { IntegrationClientsTypeahead } from '@/modules/assetProviderIntegrations/shared/IntegrationClientTypeahead/IntegrationClientsTypeahead';
import { PathsOf } from '@/types/subform';

import { useConnectHouseholdToAssetProviderMutation } from './graphql/ConnectHouseholdToAssetProviderModal.generated';

interface ConnectHouseholdToAssetProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
}

interface ConnectHouseholdForm {
  integrationClientIds: string[];
}

type ConnectHouseholdFormPaths = PathsOf<ConnectHouseholdForm>;

function ConnectHouseholdToAssetProvider({
  onClose,
  householdId,
}: Omit<ConnectHouseholdToAssetProviderModalProps, 'isOpen'>) {
  const { showFeedback } = useFeedback();
  const [updateHousehold] = useConnectHouseholdToAssetProviderMutation({
    onCompleted: () => {
      showFeedback('Successfully connected household to provider', {
        variant: 'success',
      });
      onClose();
    },
    onError: () => {
      showFeedback(
        'Failed to connect household. Please refresh the page and try again.'
      );
    },
  });
  const { formRef, handleSave } = useFormSaveHandler();
  const { handleSubmit, formState, control } =
    useFormContext<ConnectHouseholdForm>();
  const assetProviderName = useAssetProviderDisplayName({ isInline: true });
  const { canConnectToIntegrations, integrationsConnected } =
    useIsAssetProviderIntegrationEnabled();

  const onSubmit = handleSubmit((formData) => {
    void updateHousehold({
      variables: {
        id: householdId,
        input: {
          addIntegrationClientIDs: formData.integrationClientIds,
        },
      },
    });
  });

  if (canConnectToIntegrations.length === 0) return null;

  const anyIntegrationLoggedIn = canConnectToIntegrations.some((integration) =>
    integrationsConnected.includes(integration)
  );

  const heading = `Select clients in ${assetProviderName}`;
  return (
    <FormModal
      isOpen={true}
      onClose={onClose}
      heading={heading}
      fullHeight={false}
      actions={
        <FormModalActions.Provider formState={formState}>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Enable integration for this client
          </Button>
        </FormModalActions.Provider>
      }
    >
      <Stack gap={2}>
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <FormLayoutRow>
            <FormLayoutItem>
              <IntegrationClientsTypeahead<ConnectHouseholdForm>
                control={control}
                providers={canConnectToIntegrations}
                notLoggedIn={!anyIntegrationLoggedIn}
                fieldName={
                  `integrationClientIds` as const satisfies ConnectHouseholdFormPaths
                }
                label="Select integrated accounts"
                hideLabel
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </form>
      </Stack>
    </FormModal>
  );
}

export function ConnectHouseholdToAssetProviderModal(
  props: ConnectHouseholdToAssetProviderModalProps
) {
  const formMethods = useForm<ConnectHouseholdForm>({
    defaultValues: {
      integrationClientIds: [],
    },
  });

  if (!props.isOpen) return null;

  return (
    <FormProvider {...formMethods}>
      <ConnectHouseholdToAssetProvider {...props} />
    </FormProvider>
  );
}
